import { CampaignTransaction } from '../types';

export type SortField = 'date' | 'campaign' | 'creator' | 'spend' | 'url';
export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// Sort transactions by field and direction
export const sortTransactions = (
  transactions: CampaignTransaction[],
  sortConfig: SortConfig
): CampaignTransaction[] => {
  if (!sortConfig.direction) return transactions;

  return [...transactions].sort((a, b) => {
    let aValue: string | number | Date;
    let bValue: string | number | Date;

    switch (sortConfig.field) {
      case 'date':
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
        break;
      case 'campaign':
        aValue = a.campaign.toLowerCase();
        bValue = b.campaign.toLowerCase();
        break;
      case 'creator':
        aValue = a.creator.toLowerCase();
        bValue = b.creator.toLowerCase();
        break;
      case 'spend':
        aValue = a.spend;
        bValue = b.spend;
        break;
      case 'url':
        aValue = (a.url || '').toLowerCase();
        bValue = (b.url || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
};

// Format spend with color classes
export const formatSpend = (spend: number): { text: string; className: string } => {
  const formattedText = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(spend);
  
  if (spend === 0) {
    return { text: formattedText, className: 'theme-text-muted' };
  } else {
    return { text: formattedText, className: 'theme-text' };
  }
};

// Get sort icon based on current sort state
export const getSortIcon = (field: SortField, sortConfig: SortConfig): string => {
  if (sortConfig.field !== field) return '↕';
  if (sortConfig.direction === 'asc') return '↑';
  if (sortConfig.direction === 'desc') return '↓';
  return '↕';
};

// Get next sort direction when clicking a column
export const getNextSortDirection = (
  field: SortField,
  currentConfig: SortConfig
): SortDirection => {
  if (currentConfig.field !== field) return 'asc';
  if (currentConfig.direction === 'asc') return 'desc';
  if (currentConfig.direction === 'desc') return null;
  return 'asc';
};

// Get top 3 creators for collapsible feature
export const getTop3Creators = (transactions: CampaignTransaction[]): string[] => {
  const creatorTotals = new Map<string, number>();
  
  transactions.forEach(transaction => {
    const creator = transaction.creator.trim();
    if (creator) {
      creatorTotals.set(creator, (creatorTotals.get(creator) || 0) + transaction.spend);
    }
  });
  
  return Array.from(creatorTotals.entries())
    .sort((a, b) => b[1] - a[1]) // Sort by total spend descending
    .slice(0, 3)
    .map(([creator]) => creator);
};

// Filter transactions to show only top 3 creators when collapsed
export const filterToTop3Creators = (
  transactions: CampaignTransaction[],
  isCollapsed: boolean
): CampaignTransaction[] => {
  if (!isCollapsed) return transactions;
  
  const top3Creators = getTop3Creators(transactions);
  return transactions.filter(t => top3Creators.includes(t.creator.trim()));
};

// Get top 3 months by total spend for collapsible feature
export const getTop3Months = (transactions: CampaignTransaction[]): string[] => {
  const monthTotals = new Map<string, number>();
  
  transactions.forEach(transaction => {
    const month = transaction.date.slice(0, 7);
    monthTotals.set(month, (monthTotals.get(month) || 0) + transaction.spend);
  });
  
  return Array.from(monthTotals.entries())
    .sort((a, b) => b[1] - a[1]) // Sort by total spend descending
    .slice(0, 3)
    .map(([month]) => month);
};

// Filter transactions to show only top 3 months when collapsed
export const filterToTop3Months = (
  transactions: CampaignTransaction[],
  isCollapsed: boolean
): CampaignTransaction[] => {
  if (!isCollapsed) return transactions;
  
  const top3Months = getTop3Months(transactions);
  return transactions.filter(t => top3Months.includes(t.date.slice(0, 7)));
};

// Group transactions by month for summary tables
export const groupByMonth = (transactions: CampaignTransaction[]) => {
  const groups: Record<string, CampaignTransaction[]> = {};
  
  transactions.forEach(transaction => {
    const month = transaction.date.slice(0, 7);
    if (!groups[month]) {
      groups[month] = [];
    }
    groups[month].push(transaction);
  });
  
  return groups;
};

// Calculate monthly totals
export const calculateMonthlyTotals = (transactions: CampaignTransaction[]) => {
  const groups = groupByMonth(transactions);
  const totals: Record<string, number> = {};
  
  Object.keys(groups).forEach(month => {
    totals[month] = groups[month].reduce((sum, t) => sum + t.spend, 0);
  });
  
  return totals;
};

// Calculate admin fee for a given monthly spend
export const calculateAdminFee = (monthlySpend: number): number => {
  const spendOver30k = Math.max(0, monthlySpend - 30000);
  return spendOver30k * 0.15;
};

/**
 * Convert a date string to a month key (YYYY-MM format)
 * Handles YYYY-MM-DD format (slices) or parses other date formats
 * @param dateStr Date string in YYYY-MM-DD format or any parseable date
 * @returns Month key in YYYY-MM format, or null if invalid
 */
export const toMonthKey = (dateStr: string): string | null => {
  if (!dateStr) return null;
  // Fast path: if already YYYY-MM-DD format, just slice
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 7);
  // Parse other formats (ISO, M/D/YYYY, etc.)
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Convert a month key (YYYY-MM) to a human-readable label
 * @param key Month key in YYYY-MM format
 * @returns Formatted label like "September 2025"
 */
export const labelFromKey = (key: string): string => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, (m ?? 1) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}; 